import React from 'react';
import { RouteComponentProps, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { injectIntl, InjectedIntl, FormattedMessage } from 'react-intl';
import {
  Alert,
  Checkbox,
  Input,
  Select as AntSelect,
  Switch,
  notification,
  message,
} from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { SelectValue } from 'antd/lib/select';
import { Button, Modal } from '../../components';
import {
  AppState,
  fetchPlaylists,
  addPlaylistDraft,
  updatePlaylistDraft,
  savePlaylist,
  deletePlaylist,
  fetchPlaylist,
  fetchCreativities,
  fetchPlayers,
  ICreativity,
  IPlaylist,
  selectPlaylistById,
  selectPlaylistCreativities,
  selectPlaylistTargetSummary,
  selectPlaylists,
  selectPublishedCreativities,
  selectPlayersAsItem,
  selectGroupsAsItem,
  isPublishedCreativity,
} from '../../redux/modules';
import {
  Actions,
  CardGrid,
  CardTitle,
  Field,
  Footer,
  Form,
  InlineControls,
  ItemActions,
  Label,
  MutedText,
  Page,
  PlaylistCard,
  PlaylistItem,
  Section,
  SectionTitle,
  SelectionGrid,
  SelectionList,
  Title,
  Toolbar,
} from './styles';

const { Option } = AntSelect;

interface IItem {
  id: string;
  title: string;
  subtitle?: string;
}

interface IPlaylistCreativity {
  item: {
    creativity: string;
    position: number;
  };
  creativity?: ICreativity;
}

interface IProps extends RouteComponentProps<{ id?: string }> {
  intl: InjectedIntl;
  playlists: IPlaylist[];
  playlist?: IPlaylist | null;
  playlistCreativities?: IPlaylistCreativity[];
  targetSummary?: string;
  publishedCreativities: ICreativity[];
  players: IItem[];
  groups: IItem[];
  error?: any;
  fetchPlaylists: () => void;
  fetchPlaylist: (id: string) => void;
  addPlaylistDraft: () => void;
  updatePlaylistDraft: (id: string, update: Partial<IPlaylist>) => void;
  savePlaylist: (id: string) => Promise<IPlaylist | void>;
  deletePlaylist: (id: string) => void;
  fetchCreativities: () => void;
  fetchPlayers: () => void;
}

interface IState {
  deleteId: string;
}

class Playlists extends React.Component<IProps, IState> {
  state: IState = {
    deleteId: '',
  };

  componentDidMount() {
    this.props.fetchPlaylists();
    this.props.fetchCreativities();
    this.props.fetchPlayers();

    if (this.props.match.params.id && !this.isDraft()) {
      this.props.fetchPlaylist(this.props.match.params.id);
    }
  }

  componentDidUpdate(prevProps: IProps) {
    const id = this.props.match.params.id;
    const previousId = prevProps.match.params.id;

    if (id && id !== previousId && !this.isDraft()) {
      this.props.fetchPlaylist(id);
    }
  }

  isDraft = () =>
    !!this.props.match.params.id &&
    this.props.match.params.id.indexOf('draft_') === 0;

  updatePlaylist = (update: Partial<IPlaylist>) => {
    const { playlist } = this.props;

    if (playlist) {
      this.props.updatePlaylistDraft(playlist._id, update);
    }
  };

  updateTarget = (type: 'players' | 'groups', id: string, checked: boolean) => {
    const playlist = this.props.playlist;

    if (!playlist) {
      return;
    }

    const current = playlist[type] || [];
    const next = checked
      ? [...current, id]
      : current.filter(item => item !== id);

    this.updatePlaylist({
      [type]: next,
      allPlayers: false,
    } as Partial<IPlaylist>);
  };

  addCreative = (value: SelectValue) => {
    const playlist = this.props.playlist;

    if (!playlist || typeof value !== 'string') {
      return;
    }

    if (playlist.items.find(item => item.creativity === value)) {
      message.warning(
        this.props.intl.formatMessage({ id: 'playlists.message.duplicate' })
      );
      return;
    }

    this.updatePlaylist({
      items: [
        ...playlist.items,
        {
          creativity: value,
          position: playlist.items.length,
        },
      ],
    });
  };

  removeCreative = (creativeId: string) => {
    const playlist = this.props.playlist;

    if (!playlist) {
      return;
    }

    this.updatePlaylist({
      items: playlist.items
        .filter(item => item.creativity !== creativeId)
        .map((item, index) => ({ ...item, position: index })),
    });
  };

  moveCreative = (creativeId: string, direction: -1 | 1) => {
    const playlist = this.props.playlist;

    if (!playlist) {
      return;
    }

    const items = [...playlist.items].sort(
      (left, right) => left.position - right.position
    );
    const index = items.findIndex(item => item.creativity === creativeId);
    const nextIndex = index + direction;

    if (index < 0 || nextIndex < 0 || nextIndex >= items.length) {
      return;
    }

    const nextItems = [...items];
    const moving = nextItems[index];
    nextItems[index] = nextItems[nextIndex];
    nextItems[nextIndex] = moving;

    this.updatePlaylist({
      items: nextItems.map((item, index) => ({ ...item, position: index })),
    });
  };

  save = async () => {
    const playlist = this.props.playlist;

    if (playlist) {
      const isDraft = playlist._id.indexOf('draft_') === 0;
      const savedPlaylist = await this.props.savePlaylist(playlist._id);

      if (isDraft && savedPlaylist) {
        notification.open({
          message: this.props.intl.formatMessage({ id: 'success' }),
          className: 'success',
          description: this.props.intl.formatMessage({
            id: 'playlists.message.created',
          }),
          duration: 45,
        });
      }
    }
  };

  confirmDelete = () => {
    if (this.state.deleteId) {
      this.props.deletePlaylist(this.state.deleteId);
      this.setState({ deleteId: '' });
    }
  };

  renderList() {
    const { intl, playlists } = this.props;

    return (
      <Page>
        <Toolbar>
          <Title>
            <FormattedMessage id="playlists.title" />
          </Title>
          <Button
            button={intl.formatMessage({ id: 'playlists.add' })}
            onClick={this.props.addPlaylistDraft}
          />
        </Toolbar>
        <CardGrid>
          {playlists.map(playlist => (
            <PlaylistCard key={playlist._id}>
              <CardTitle>{playlist.name || '-'}</CardTitle>
              <MutedText>
                {playlist.active
                  ? intl.formatMessage({ id: 'playlists.active' })
                  : intl.formatMessage({ id: 'playlists.inactive' })}
              </MutedText>
              <MutedText>
                {playlist.allPlayers
                  ? intl.formatMessage({ id: 'playlists.allScreens' })
                  : [
                      playlist.players && playlist.players.length
                        ? intl.formatMessage(
                            { id: 'playlists.screensCount' },
                            { count: playlist.players.length }
                          )
                        : '',
                      playlist.groups && playlist.groups.length
                        ? intl.formatMessage(
                            { id: 'playlists.groupsCount' },
                            { count: playlist.groups.length }
                          )
                        : '',
                    ]
                      .filter(Boolean)
                      .join(', ') ||
                    intl.formatMessage({ id: 'playlists.noTarget' })}
              </MutedText>
              <MutedText>
                <FormattedMessage
                  id="playlists.itemsCount"
                  values={{ count: playlist.items.length }}
                />
              </MutedText>
              <Actions>
                <Link to={`/playlists/${playlist._id}`}>
                  <Button
                    type="whiteBlue"
                    button={intl.formatMessage({ id: 'playlists.edit' })}
                  />
                </Link>
                <Button
                  type="whiteRed"
                  button={intl.formatMessage({ id: 'playlists.delete' })}
                  onClick={() => this.setState({ deleteId: playlist._id })}
                />
              </Actions>
            </PlaylistCard>
          ))}
        </CardGrid>
      </Page>
    );
  }

  renderTargetSelection() {
    const playlist = this.props.playlist;

    if (!playlist) {
      return null;
    }

    return (
      <SelectionGrid>
        <SelectionList>
          <Label>
            <FormattedMessage id="playlists.groups" />
          </Label>
          {this.props.groups.map(group => (
            <div key={group.id}>
              <Checkbox
                disabled={!!playlist.allPlayers}
                checked={!!(playlist.groups || []).includes(group.id)}
                onChange={(event: CheckboxChangeEvent) =>
                  this.updateTarget('groups', group.id, event.target.checked)
                }
              >
                {group.title}
              </Checkbox>
            </div>
          ))}
        </SelectionList>
        <SelectionList>
          <Label>
            <FormattedMessage id="playlists.screens" />
          </Label>
          {this.props.players.map(player => (
            <div key={player.id}>
              <Checkbox
                disabled={!!playlist.allPlayers}
                checked={!!(playlist.players || []).includes(player.id)}
                onChange={(event: CheckboxChangeEvent) =>
                  this.updateTarget('players', player.id, event.target.checked)
                }
              >
                {player.title}
              </Checkbox>
            </div>
          ))}
        </SelectionList>
      </SelectionGrid>
    );
  }

  renderItems() {
    const {
      intl,
      playlist,
      playlistCreativities,
      publishedCreativities,
    } = this.props;

    if (!playlist) {
      return null;
    }

    const selectedIds = playlist.items.map(item => item.creativity);
    const selectableCreativities = publishedCreativities.filter(
      creativity => !selectedIds.includes(creativity._id)
    );

    return (
      <>
        <Field>
          <Label>
            <FormattedMessage id="playlists.addCreative" />
          </Label>
          <AntSelect
            style={{ width: 320 }}
            value={undefined}
            onChange={this.addCreative}
            placeholder={intl.formatMessage({
              id: 'playlists.addCreativePlaceholder',
            })}
          >
            {selectableCreativities.map(creativity => (
              <Option key={creativity._id} value={creativity._id}>
                {creativity.name || creativity._id}
              </Option>
            ))}
          </AntSelect>
        </Field>
        {(playlistCreativities || []).map((entry, index) => {
          const invalid = !isPublishedCreativity(entry.creativity);
          const name =
            (entry.creativity && entry.creativity.name) ||
            entry.item.creativity;

          return (
            <PlaylistItem key={entry.item.creativity} invalid={invalid}>
              <div>
                <strong>
                  {index + 1}. {name}
                </strong>
                {invalid && (
                  <MutedText>
                    <FormattedMessage id="playlists.invalidCreative" />
                  </MutedText>
                )}
              </div>
              <ItemActions>
                <Button
                  type="grey"
                  disabled={index === 0}
                  button={intl.formatMessage({ id: 'playlists.moveUp' })}
                  onClick={() => this.moveCreative(entry.item.creativity, -1)}
                />
                <Button
                  type="grey"
                  disabled={index === playlist.items.length - 1}
                  button={intl.formatMessage({ id: 'playlists.moveDown' })}
                  onClick={() => this.moveCreative(entry.item.creativity, 1)}
                />
                <Button
                  type="whiteRed"
                  button={intl.formatMessage({ id: 'playlists.remove' })}
                  onClick={() => this.removeCreative(entry.item.creativity)}
                />
              </ItemActions>
            </PlaylistItem>
          );
        })}
      </>
    );
  }

  renderEditor() {
    const { error, intl, playlist, targetSummary } = this.props;

    if (!playlist) {
      return (
        <Page>
          <FormattedMessage id="playlists.loading" />
        </Page>
      );
    }

    return (
      <Page>
        <Toolbar>
          <Title>
            {playlist.name || intl.formatMessage({ id: 'playlists.new' })}
          </Title>
          <Link to="/playlists">
            <Button
              type="whiteBlue"
              button={intl.formatMessage({ id: 'playlists.back' })}
            />
          </Link>
        </Toolbar>
        <Form>
          {Array.isArray(error) &&
            error.length > 0 && (
              <Alert
                type="error"
                showIcon
                message={error
                  .map((id: string) => intl.formatMessage({ id }))
                  .join(', ')}
              />
            )}
          <Section>
            <SectionTitle>
              <FormattedMessage id="playlists.details" />
            </SectionTitle>
            <Field>
              <Label>
                <FormattedMessage id="playlists.name" />
              </Label>
              <Input
                value={playlist.name}
                onChange={event =>
                  this.updatePlaylist({ name: event.target.value })
                }
              />
            </Field>
            <SectionTitle>
              <FormattedMessage id="playlists.creatives" />
            </SectionTitle>
            {this.renderItems()}
            <InlineControls>
              <Switch
                checked={playlist.active}
                onChange={active => this.updatePlaylist({ active })}
              />
              <FormattedMessage id="playlists.active" />
            </InlineControls>
          </Section>
          <Section>
            <SectionTitle>
              <FormattedMessage id="playlists.targets" />
            </SectionTitle>
            <Field>
              <Checkbox
                checked={!!playlist.allPlayers}
                onChange={(event: CheckboxChangeEvent) =>
                  this.updatePlaylist({
                    allPlayers: event.target.checked,
                    ...(event.target.checked
                      ? { players: [], groups: [] }
                      : {}),
                  })
                }
              >
                <FormattedMessage id="playlists.allScreens" />
              </Checkbox>
            </Field>
            <MutedText>{targetSummary}</MutedText>
            {this.renderTargetSelection()}
          </Section>
          <Footer>
            <Button
              button={intl.formatMessage({ id: 'playlists.save' })}
              onClick={this.save}
            />
            <Button
              type="whiteRed"
              button={intl.formatMessage({ id: 'playlists.delete' })}
              onClick={() => this.setState({ deleteId: playlist._id })}
            />
          </Footer>
        </Form>
      </Page>
    );
  }

  render() {
    const { intl, match } = this.props;

    return (
      <>
        <Modal
          title={intl.formatMessage({ id: 'playlists.deleteTitle' })}
          visible={!!this.state.deleteId}
          noFooter
        >
          <p>
            <FormattedMessage id="playlists.deleteText" />
          </p>
          <Actions>
            <Button
              type="whiteBlue"
              button={intl.formatMessage({ id: 'actions.modalCancel' })}
              onClick={() => this.setState({ deleteId: '' })}
            />
            <Button
              type="whiteRed"
              button={intl.formatMessage({ id: 'actions.deleteModalConfirm' })}
              onClick={this.confirmDelete}
            />
          </Actions>
        </Modal>
        {match.params.id ? this.renderEditor() : this.renderList()}
      </>
    );
  }
}

export default connect(
  (state: AppState, props: IProps) => {
    const id = props.match.params.id || '';

    return {
      playlists: selectPlaylists(state),
      playlist: id ? selectPlaylistById(state, id) : null,
      playlistCreativities: id ? selectPlaylistCreativities(id)(state) : [],
      targetSummary: id ? selectPlaylistTargetSummary(id)(state) : '',
      publishedCreativities: selectPublishedCreativities(state),
      players: selectPlayersAsItem(state),
      groups: selectGroupsAsItem(state),
      error: state.playlists.error,
    };
  },
  {
    fetchPlaylists,
    fetchPlaylist,
    addPlaylistDraft,
    updatePlaylistDraft,
    savePlaylist,
    deletePlaylist,
    fetchCreativities,
    fetchPlayers,
  }
)(injectIntl<any>(Playlists));
